<template>
  <article class="kpi-card">
    <div class="kpi-icon" :style="iconStyle">
      <i class="fa" :class="icon" aria-hidden="true"></i>
    </div>

    <div class="kpi-copy">
      <p>{{ title }}</p>
      <div class="kpi-values">
        <h3
          :style="valueStyle"
          :class="{ 'kpi-value-truncated': truncateValue }"
          :title="truncateValue ? value : ''"
        >
          <router-link
            v-if="hasValueRoute"
            :to="valueRoute"
            class="kpi-value-link"
            :class="[valueLinkClass, { 'kpi-value-link-truncated': truncateValue }]"
            :title="truncateValue ? value : ''"
          >
            {{ value }}
          </router-link>
          <span
            v-else
            class="kpi-value-text"
            :class="{ 'kpi-value-text-truncated': truncateValue }"
            :title="truncateValue ? value : ''"
          >
            {{ value }}
          </span>
        </h3>

        <small v-if="normalizedValueMeta" class="kpi-value-meta">{{ normalizedValueMeta }}</small>

        <ul v-if="normalizedExtraValues.length > 0" class="kpi-extra-values">
          <li v-for="(line, index) in normalizedExtraValues" :key="`extra-${title}-${index}`">
            {{ line }}
          </li>
        </ul>
      </div>
    </div>
  </article>
</template>

<script>
export default {
  name: 'SalesMultiCustomerKpiCard',
  props: {
    title: {
      type: String,
      required: true,
    },
    value: {
      type: String,
      required: true,
    },
    valueRoute: {
      type: [Object, String],
      default: null,
    },
    valueLinkClass: {
      type: String,
      default: '',
    },
    valueScale: {
      type: Number,
      default: 1,
    },
    truncateValue: {
      type: Boolean,
      default: false,
    },
    valueMeta: {
      type: String,
      default: '',
    },
    extraValues: {
      type: Array,
      default: () => [],
    },
    icon: {
      type: String,
      default: 'fa-circle',
    },
    iconBg: {
      type: String,
      default: '#e8f3ff',
    },
    iconColor: {
      type: String,
      default: '#0070cd',
    },
  },
  computed: {
    iconStyle() {
      return {
        background: this.iconBg,
        color: this.iconColor,
      };
    },
    hasValueRoute() {
      if (typeof this.valueRoute === 'string') {
        return String(this.valueRoute || '').trim() !== '';
      }

      return !!this.valueRoute && typeof this.valueRoute === 'object';
    },
    valueStyle() {
      const safeScale = Number(this.valueScale);
      const scale = Number.isFinite(safeScale) && safeScale > 0 ? safeScale : 1;

      return {
        fontSize: `${24 * scale}px`,
      };
    },
    normalizedExtraValues() {
      return (Array.isArray(this.extraValues) ? this.extraValues : [])
        .map((item) => String(item || '').trim())
        .filter(Boolean);
    },
    normalizedValueMeta() {
      return String(this.valueMeta || '').trim();
    },
  },
};
</script>

<style scoped>
.kpi-card {
  background: #ffffff;
  border: 1px solid rgba(153, 152, 153, 0.2);
  border-radius: 9px;
  box-shadow: 0 3px 12px rgba(0, 98, 163, 0.08);
  padding: 14px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.kpi-icon {
  width: 40px;
  height: 40px;
  border-radius: 7.5px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  flex-shrink: 0;
}

.kpi-copy p {
  margin: 0;
  font-size: 11px;
  text-transform: uppercase;
  font-weight: 700;
  color: #999899;
  letter-spacing: 0.02em;
}

.kpi-copy {
  flex: 1;
  min-width: 0;
}

.kpi-values {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  width: 100%;
  min-width: 0;
}

.kpi-values h3 {
  margin: 2px 0 0;
  font-weight: 700;
  color: #494949;
  line-height: 1.2;
  width: 100%;
  min-width: 0;
}

.kpi-value-link {
  color: inherit;
  text-decoration: underline;
}

.kpi-value-text {
  color: inherit;
}

.kpi-value-link:hover {
  color: inherit;
}

.kpi-value-link-customer {
  color: #0070cd;
  text-decoration: none;
}

.kpi-value-link-customer:hover {
  color: #0070cd;
  text-decoration: none;
}

.kpi-value-truncated {
  overflow: hidden;
}

.kpi-value-link-truncated,
.kpi-value-text-truncated {
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: bottom;
}

.kpi-value-meta {
  display: block;
  margin-top: 2px;
  font-size: 11px;
  text-transform: none;
  font-weight: 400;
  line-height: 1.2;
  color: #6b7280;
}

.kpi-extra-values {
  margin: 0;
  padding: 0;
  list-style: none;
  display: grid;
  gap: 2px;
  color: #494949;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.2;
}
</style>
